import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, RefreshCw, Trash2, ExternalLink, AlertTriangle, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin → Duplicados. Lists cross-provider duplicate clusters detected either
// via an explicit publish-anyway bypass (kind="bypass") or via a retrospective
// scan of active/pending/scheduled events (kind="detected"). Admins can open
// each event in a new tab, and delete the redundant copy directly from here.
export default function DuplicatesTab() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({ clusters: [], total_events_in_duplicates: 0 });
  const [deletingId, setDeletingId] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/events/duplicates`);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar el reporte de duplicados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Guard against React StrictMode double-invoke (dev only) — the report
    // scan is a relatively heavy admin call, no reason to fire it twice.
    if (didMountRef.current) return;
    didMountRef.current = true;
    fetchReport();
  }, [fetchReport]);
  const didMountRef = useRef(false);

  const deleteEvent = async (id, name) => {
    if (!window.confirm(`Eliminar el evento "${name}"?\n\nEsta acción no se puede deshacer.`)) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/api/events/${id}`);
      toast.success('Evento eliminado');
      setReport((prev) => {
        const clusters = prev.clusters
          .map((c) => ({ ...c, events: c.events.filter((e) => e.id !== id) }))
          .filter((c) => c.events.length >= 2);
        const total = clusters.reduce((n, c) => n + c.events.length, 0);
        return { clusters, total_events_in_duplicates: total };
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeletingId(null);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' });
    } catch { return iso; }
  };

  const bypassCount = report.clusters.filter((c) => c.kind === 'bypass').length;
  const detectedCount = report.clusters.filter((c) => c.kind === 'detected').length;

  return (
    <div className="space-y-4" data-testid="duplicates-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            Duplicados
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Eventos que se solapan por nombre + fecha (±6h) + recinto o comuna. Elimina el sobrante para dejar solo la versión canónica.
          </p>
        </div>
        <Button variant="outline" onClick={fetchReport} disabled={loading} data-testid="duplicates-refresh">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Actualizar
        </Button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700">
          {bypassCount} publicados con bypass
        </Badge>
        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
          {detectedCount} clusters detectados
        </Badge>
        <Badge variant="outline" className="border-gray-300 bg-gray-50 text-gray-700">
          {report.total_events_in_duplicates} eventos en total
        </Badge>
      </div>

      {loading && report.clusters.length === 0 && (
        <div className="text-center py-16 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
          Cargando reporte…
        </div>
      )}

      {!loading && report.clusters.length === 0 && (
        <div className="text-center py-16 text-gray-500 bg-white rounded-lg border border-dashed">
          <div className="text-5xl mb-2">✨</div>
          <p className="font-medium">No hay duplicados detectados</p>
          <p className="text-sm mt-1">La plataforma está limpia. Buen trabajo.</p>
        </div>
      )}

      <div className="space-y-4">
        {report.clusters.map((cluster) => (
          <div
            key={cluster.signature}
            className={`rounded-lg border ${cluster.kind === 'bypass' ? 'border-red-200 bg-red-50/40' : 'border-amber-200 bg-amber-50/40'} p-4`}
            data-testid={`duplicate-cluster-${cluster.signature}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Badge className={cluster.kind === 'bypass' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}>
                {cluster.kind === 'bypass' ? '⚠️ Bypass explícito' : '🔍 Detectado'}
              </Badge>
              <span className="text-xs text-gray-500 font-mono truncate">{cluster.signature}</span>
            </div>
            <div className="space-y-2">
              {cluster.events.map((ev, idx) => (
                <div
                  key={ev.id}
                  className="flex items-center gap-3 bg-white p-3 rounded border border-gray-200"
                  data-testid={`duplicate-event-${ev.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">#{idx + 1}</span>
                      <span className="font-semibold text-sm truncate">{ev.name}</span>
                      <Badge variant="outline" className="text-[10px]">{ev.status}</Badge>
                      {ev.duplicate_of_id && (
                        <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">
                          bypass → {ev.duplicate_of_id.slice(0, 8)}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-1 flex items-center gap-3 flex-wrap">
                      <span>📅 {fmt(ev.start_date)}</span>
                      {ev.venue_name && <span>📍 {ev.venue_name}</span>}
                      {ev.commune_name && <span>· {ev.commune_name}</span>}
                      {ev.provider_name && <span>· 👤 {ev.provider_name}</span>}
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {ev.views}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a
                      href={`/event/${ev.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                      title="Ver evento"
                      data-testid={`duplicate-view-${ev.id}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 h-8"
                      onClick={() => deleteEvent(ev.id, ev.name)}
                      disabled={deletingId === ev.id}
                      data-testid={`duplicate-delete-${ev.id}`}
                    >
                      {deletingId === ev.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
